"use client";

import React from "react";
import type { Control, FieldErrors, UseFormRegister } from "react-hook-form";
import { Controller } from "react-hook-form";
import Button from "../button";
import Input from "../form/input";
import RadioToggle from "../radioToggle";
import type { AsaasSubaccountCreateForm } from "@/validations/asaas";

interface BankAccountFormProps {
  accountType: "PF" | "PJ";
  onAccountTypeChange: (type: "PF" | "PJ") => void;
  register: UseFormRegister<AsaasSubaccountCreateForm>;
  control: Control<AsaasSubaccountCreateForm>;
  errors: FieldErrors<AsaasSubaccountCreateForm>;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const BankAccountForm: React.FC<BankAccountFormProps> = ({
  accountType,
  onAccountTypeChange,
  register,
  control,
  errors,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const isCompanyAccount = accountType === "PJ";
  const accountNameLabel = isCompanyAccount ? "Razão social" : "Nome completo";
  const documentLabel = isCompanyAccount ? "CNPJ" : "CPF";
  const documentPlaceholder = isCompanyAccount
    ? "Somente números do CNPJ"
    : "Somente números do CPF";

  return (
    <form
      className="max-h-[75vh] space-y-6 overflow-y-auto pr-1"
      onSubmit={onSubmit}
    >
      <p className="text-sm text-gray-600">
        Informe os dados do responsável financeiro para criarmos a conta de
        recebimento. Esses dados serão validados pelo provedor bancário.
      </p>

      <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <header className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              Dados do responsável
            </h3>
            <span className="text-xs text-gray-500">
              Utilize informações idênticas às do documento oficial.
            </span>
          </div>
          <RadioToggle
            name="Tipo de conta"
            value={accountType}
            options={[
              { value: "PF", label: "Conta pessoa física" },
              { value: "PJ", label: "Conta empresa" },
            ]}
            onChange={(value) => onAccountTypeChange(value as "PF" | "PJ")}
            className="w-full md:w-auto"
          />
        </header>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Input
              label={accountNameLabel}
              placeholder={
                isCompanyAccount ? "Informe a razão social" : "Informe o nome"
              }
              validation={
                errors.name
                  ? { type: "error", message: errors.name.message as string }
                  : undefined
              }
              {...register("name")}
            />
          </div>

          <Controller
            name="cpfCnpj"
            control={control}
            render={({ field }) => (
              <div className="space-y-1">
                <Input
                  label={documentLabel}
                  placeholder={documentPlaceholder}
                  value={field.value}
                  onChange={(event: any) =>
                    field.onChange(
                      event.target.value
                        .replace(/\D/g, "")
                        .slice(0, isCompanyAccount ? 14 : 11),
                    )
                  }
                  validation={
                    errors.cpfCnpj
                      ? {
                          type: "error",
                          message: errors.cpfCnpj.message as string,
                        }
                      : undefined
                  }
                />
              </div>
            )}
          />

          <div className="space-y-1">
            <Input
              label="Email principal"
              placeholder="email@empresa.com"
              validation={
                errors.email
                  ? { type: "error", message: errors.email.message as string }
                  : undefined
              }
              {...register("email")}
            />
          </div>

          <Controller
            name="mobilePhone"
            control={control}
            render={({ field }) => (
              <div className="space-y-1">
                <Input
                  label="Celular"
                  placeholder="DDD + número"
                  value={field.value}
                  onChange={(event: any) =>
                    field.onChange(event.target.value.replace(/\D/g, ""))
                  }
                  validation={
                    errors.mobilePhone
                      ? {
                          type: "error",
                          message: errors.mobilePhone.message as string,
                        }
                      : undefined
                  }
                />
              </div>
            )}
          />

          {!isCompanyAccount && (
            <div className="space-y-1">
              <Input
                label="Data de nascimento"
                type="date"
                validation={
                  errors.birthDate
                    ? {
                        type: "error",
                        message: errors.birthDate.message as string,
                      }
                    : undefined
                }
                {...register("birthDate")}
              />
            </div>
          )}

          <div className="space-y-1">
            <Input
              label="Renda mensal"
              type="number"
              min={0}
              step="0.01"
              placeholder="0,00"
              validation={
                errors.incomeValue
                  ? {
                      type: "error",
                      message: errors.incomeValue.message as string,
                    }
                  : undefined
              }
              {...register("incomeValue", { valueAsNumber: true })}
            />
          </div>

          {isCompanyAccount && (
            <div className="space-y-1">
              <label className="mb-2 block text-sm font-medium">
                Tipo de empresa
              </label>
              <select
                className={`w-full rounded-md border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 ${errors.companyType ? "border-red-500" : "border-gray-300"}`}
                {...register("companyType")}
                defaultValue=""
              >
                <option value="">Selecione</option>
                <option value="MEI">MEI</option>
                <option value="LIMITED">LTDA</option>
                <option value="INDIVIDUAL">Empresário individual</option>
                <option value="ASSOCIATION">Associação</option>
              </select>
              {errors.companyType && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.companyType.message as string}
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <header className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">
            Endereço comercial
          </h3>
          <span className="text-xs text-gray-500">
            Informe o CEP primeiro para preencher os demais campos
            automaticamente.
          </span>
        </header>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Controller
            name="address.postalCode"
            control={control}
            render={({ field }) => (
              <div className="space-y-1">
                <Input
                  label="CEP"
                  isCep
                  value={field.value}
                  onChange={field.onChange}
                  validation={
                    errors.address?.postalCode
                      ? {
                          type: "error",
                          message: errors.address.postalCode.message as string,
                        }
                      : undefined
                  }
                />
              </div>
            )}
          />

          <div className="space-y-1">
            <Input
              label="Rua"
              placeholder="Nome da rua"
              validation={
                errors.address?.street
                  ? {
                      type: "error",
                      message: errors.address.street.message as string,
                    }
                  : undefined
              }
              {...register("address.street")}
            />
          </div>

          <div className="space-y-1">
            <Input
              label="Número"
              placeholder="Número"
              validation={
                errors.address?.streetNumber
                  ? {
                      type: "error",
                      message: errors.address.streetNumber.message as string,
                    }
                  : undefined
              }
              {...register("address.streetNumber")}
            />
          </div>

          <div className="space-y-1">
            <Input
              label="Bairro"
              placeholder="Bairro"
              validation={
                errors.address?.district
                  ? {
                      type: "error",
                      message: errors.address.district.message as string,
                    }
                  : undefined
              }
              {...register("address.district")}
            />
          </div>

          <div className="space-y-1">
            <Input
              label="Cidade"
              placeholder="Cidade"
              validation={
                errors.address?.city
                  ? {
                      type: "error",
                      message: errors.address.city.message as string,
                    }
                  : undefined
              }
              {...register("address.city")}
            />
          </div>

          <div className="space-y-1">
            <Input
              label="UF"
              placeholder="UF"
              className="uppercase"
              validation={
                errors.address?.state
                  ? {
                      type: "error",
                      message: errors.address.state.message as string,
                    }
                  : undefined
              }
              {...register("address.state", {
                setValueAs: (value: unknown) =>
                  typeof value === "string"
                    ? value.toUpperCase().slice(0, 2)
                    : "",
              })}
            />
          </div>
        </div>
      </section>

      <div className="flex items-center justify-end gap-3 pt-2">
        <Button onClick={onCancel} text="Cancelar" variant="danger" />
        <Button
          text={isSubmitting ? "Enviando..." : "Criar conta"}
          variant="success"
          type="submit"
          disabled={isSubmitting}
          isLoading={isSubmitting}
        />
      </div>
    </form>
  );
};

export default BankAccountForm;
